import React, { useState, Component } from 'react';
/**
 * ExplanationContent — Conversational explanation with Djay intros.
 *
 * Shows distractor explanation (why wrong) and main explanation (how to solve)
 * with Djay avatar intros and numbered solution steps.
 *
 * Co-located in practice/ domain (CL-1 s64).
 */
import { motion } from 'framer-motion';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
import { DjayIdentity, getDjayDistractorIntro, getDjayExplanationIntro } from '../../../mascot';
import { ExplanationSteps } from './ExplanationSteps';
// ── Types ────────────────────────────────────────────────────
interface ExplanationContentProps {
  explanation: string;
  distractorExplanation?: string;
  isCorrect: boolean;
  solutionSteps?: string[];
}
// ── Component ────────────────────────────────────────────────
export function ExplanationContent({
  explanation,
  distractorExplanation,
  isCorrect,
  solutionSteps
}: ExplanationContentProps) {
  // useState initializer runs once — prevents random re-pick on scroll/re-render
  const [distractorIntro] = useState(() => getDjayDistractorIntro());
  const [explanationIntro] = useState(() => getDjayExplanationIntro());
  return <div className="space-y-5" data-id="element-11259" data-mp="25ecfg9o9zsxo">
      {/* Distractor explanation (why it was wrong) */}
      {!isCorrect && distractorExplanation && <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3
    }} data-id="element-11260" data-mp="20eztrs954rwo">
        
          <div className="flex items-start gap-3 mb-4" data-id="element-11261" data-mp="1qh3lr9bi2d2s">
            <motion.div className="flex-shrink-0 mt-0.5" animate={{
          rotate: [0, -5, 5, 0]
        }} transition={{
          delay: 0.3,
          duration: 0.5
        }} data-id="element-11262" data-mp="1qgae864pvjjl">
            
              <DjayIdentity size="sm" emotion="thinking" animate={true} data-id="element-11263" data-mp="1hirlefku00x5" />
            </motion.div>
            <div className="min-w-0 flex-1" data-id="element-11264" data-mp="3gt9iinhaoio1">
              <h4 className="font-bold text-slate-700 text-sm mb-2" data-id="element-11265" data-mp="3bueriy96bnsu">
                {distractorIntro}
              </h4>
              <div className="text-slate-600 text-sm leading-relaxed" data-id="element-11266" data-mp="3uyi788zqi2nl">
                <LatexRenderer content={distractorExplanation} data-id="element-11267" data-mp="3knd96m4po14" />
              </div>
            </div>
          </div>
          <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent my-5" data-id="element-11268" data-mp="3lzo4s1zq2i24" />
        </motion.div>}

      {/* Main explanation (how to solve it) */}
      <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: distractorExplanation ? 0.15 : 0,
      duration: 0.3
    }} className="relative" data-id="element-11269" data-mp="ii672ndnyrff">
        
        <div className="flex items-center gap-2 mb-3" data-id="element-11270" data-mp="8ix63akdscjk">
          <motion.div animate={{
          y: [0, -2, 0]
        }} transition={{
          delay: 0.4,
          duration: 1.5,
          repeat: Infinity,
          repeatType: 'reverse'
        }} data-id="element-11271" data-mp="8mcksca57jkg">
            
            <DjayIdentity size="sm" emotion="pointing" animate={true} data-id="element-11272" data-mp="2y90uxb68xicx" />
          </motion.div>
          <span className="text-sm font-bold text-orange-600" data-id="element-11273" data-mp="2qprn3mde1rpz">
            {explanationIntro}
          </span>
        </div>

        <ExplanationSteps text={explanation || (isCorrect ? 'Goed gedaan! Je hebt de juiste strategie gebruikt.' : 'Kijk goed naar de stappen hierboven.')} steps={solutionSteps} variant="conversational" data-id="element-11274" data-mp="2lrhmgw8w9ywz" />
        
      </motion.div>
    </div>;
}